/**
 * AdminAuditTab - Log of admin / support-bot actions on user accounts
 * (cai_admin_audit_logs). Filterable by target user, actor and action.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAdminAuditLogs, type AdminAuditLog } from '@/services/adminService';
import { AUDIT_ACTION_LABELS, AuditLogList } from './user-detail/shared';

const LIMIT = 25;

const AdminAuditTab: React.FC = () => {
  const [logs, setLogs] = useState<AdminAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ userId: '', actorId: '', action: '' });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = { page: String(page), limit: String(LIMIT) };
      for (const [key, value] of Object.entries(appliedFilters)) {
        if (value.trim()) params[key] = value.trim();
      }
      const result = await getAdminAuditLogs(params);
      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar auditoria');
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      {error && <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{error}</div>}

      <h2 className="text-lg font-semibold">Auditoria ({total})</h2>

      <div className="flex flex-wrap gap-2">
        <Input
          value={filters.userId}
          onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="ID do usuário alvo"
          className="flex-1 min-w-[14rem]"
        />
        <Input
          value={filters.actorId}
          onChange={(e) => setFilters({ ...filters, actorId: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="ID do admin"
          className="flex-1 min-w-[14rem]"
        />
        <select
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Todas as ações</option>
          {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <Button onClick={handleSearch} disabled={loading}>Filtrar</Button>
      </div>

      <div className="border border-border rounded-lg p-4">
        {loading ? <p className="text-muted-foreground">Carregando...</p> : <AuditLogList logs={logs} showTarget />}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Pagina {page} de {totalPages}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setPage(page - 1)} disabled={page <= 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditTab;
